import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { MemberSelect } from '@/features/members';
import { fromLocalInput } from '@/lib/datetime';
import { BID_CHANNELS, BID_JOB_TYPES, BID_STATUSES, valuesOf } from '../constants';
import type { BidInput } from '../types';

// Decimals travel as strings (DRF DecimalField); empty = null.
const decimal = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d+(\.\d{1,2})?$/.test(v), 'Enter a number (up to 2 decimals)');
const integer = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d+$/.test(v), 'Enter a whole number');

const schema = z.object({
  channel: z.enum(valuesOf(BID_CHANNELS)),
  job_id: z.string().trim().min(1, 'Job id is required').max(128),
  proposal_id: z.string().trim().max(128),
  bidder_profile: z.string().trim().max(120),
  owner: z.string(),
  job_title: z.string().trim().max(255),
  job_url: z.string().trim().url('Enter a full URL').or(z.literal('')),
  job_type: z.enum([...valuesOf(BID_JOB_TYPES), '']),
  client_budget: z.string().trim().max(64),
  experience_level: z.string().trim().max(32),
  submitted_at: z.string(),
  currency: z.string().trim().length(3, 'ISO code, e.g. USD').toUpperCase(),
  bid_rate: decimal,
  bid_amount: decimal,
  profile_rate_at_submit: decimal,
  boosted: z.boolean(),
  connects_cost: integer,
  connects_spent: integer,
  connects_refunded: integer,
  bid_filter_verdict: z.string(),
  bid_range_low: decimal,
  bid_range_median: decimal,
  bid_range_high: decimal,
  client_country: z.string().trim().max(64),
  client_hire_rate_pct: decimal,
  client_total_spent: decimal,
  status: z.enum(valuesOf(BID_STATUSES)),
  viewed_by_client: z.boolean(),
  replied_at: z.string(),
  room_id: z.string().trim().max(128),
  contract_id: z.string().trim().max(128),
});

export type BidFormValues = z.infer<typeof schema>;

const FIELD_KEYS = Object.keys(schema.shape) as (keyof BidFormValues)[];

const EMPTY: BidFormValues = {
  channel: 'upwork',
  job_id: '',
  proposal_id: '',
  bidder_profile: '',
  owner: '',
  job_title: '',
  job_url: '',
  job_type: 'hourly',
  client_budget: '',
  experience_level: '',
  submitted_at: '',
  currency: 'USD',
  bid_rate: '',
  bid_amount: '',
  profile_rate_at_submit: '',
  boosted: false,
  connects_cost: '',
  connects_spent: '',
  connects_refunded: '',
  bid_filter_verdict: '',
  bid_range_low: '',
  bid_range_median: '',
  bid_range_high: '',
  client_country: '',
  client_hire_rate_pct: '',
  client_total_spent: '',
  status: 'submitted',
  viewed_by_client: false,
  replied_at: '',
  room_id: '',
  contract_id: '',
};

const orNull = (v: string) => (v === '' ? null : v);
const intOrNull = (v: string) => (v === '' ? null : Number(v));

/** Keys whose form value is a string — the ones `text()` may render. */
type TextKey = {
  [K in keyof BidFormValues]: BidFormValues[K] extends string ? K : never;
}[keyof BidFormValues];

interface BidFormProps {
  defaultValues?: Partial<BidFormValues>;
  submitLabel: string;
  /**
   * `create` sends the whole payload. `edit` sends **only the fields the user
   * changed** — a synced bid must not have untouched fields re-sent (a
   * round-tripped datetime loses its seconds, and every re-sent field would be
   * remembered as human-edited and frozen against the sync).
   */
  mode: 'create' | 'edit';
  /** Synced rows keep their key: changing channel / job id / proposal id would make the next sync create a duplicate. */
  lockKey?: boolean;
  onSubmit: (values: Partial<BidInput>) => Promise<unknown>;
  onCancel: () => void;
}

export function BidForm({
  defaultValues,
  submitLabel,
  mode,
  lockKey = false,
  onSubmit,
  onCancel,
}: BidFormProps) {
  const form = useForm<BidFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { ...EMPTY, ...defaultValues },
  });

  async function handleSubmit(v: BidFormValues) {
    const full: BidInput = {
      channel: v.channel,
      job_id: v.job_id,
      proposal_id: v.proposal_id,
      bidder_profile: v.bidder_profile,
      owner: v.owner ? Number(v.owner) : null,
      job_title: v.job_title,
      job_url: v.job_url,
      job_type: v.job_type,
      client_budget: v.client_budget,
      experience_level: v.experience_level,
      connects_cost: intOrNull(v.connects_cost),
      submitted_at: fromLocalInput(v.submitted_at),
      currency: v.currency,
      bid_rate: orNull(v.bid_rate),
      bid_amount: orNull(v.bid_amount),
      profile_rate_at_submit: orNull(v.profile_rate_at_submit),
      boosted: v.boosted,
      connects_spent: intOrNull(v.connects_spent),
      connects_refunded: intOrNull(v.connects_refunded),
      bid_filter_verdict: v.bid_filter_verdict,
      bid_range_low: orNull(v.bid_range_low),
      bid_range_median: orNull(v.bid_range_median),
      bid_range_high: orNull(v.bid_range_high),
      client_country: v.client_country,
      client_hire_rate_pct: orNull(v.client_hire_rate_pct),
      client_total_spent: orNull(v.client_total_spent),
      viewed_by_client: v.viewed_by_client,
      replied_at: fromLocalInput(v.replied_at),
      room_id: v.room_id,
      contract_id: v.contract_id,
      status: v.status,
    };
    let input: Partial<BidInput> = full;
    if (mode === 'edit') {
      const dirty = Object.keys(form.formState.dirtyFields) as (keyof BidInput)[];
      // The hourly/fixed figure is one concept: a job-type switch sends both.
      if (dirty.includes('job_type')) dirty.push('bid_rate', 'bid_amount');
      input = Object.fromEntries(dirty.filter((k) => k in full).map((k) => [k, full[k]]));
    }
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;
  const jobType = useWatch({ control: form.control, name: 'job_type' });

  const text = (
    name: TextKey,
    label: string,
    opts: {
      placeholder?: string;
      type?: string;
      hint?: string;
      className?: string;
      disabled?: boolean;
    } = {},
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={opts.className}>
          <FormLabel className="flex items-center gap-1.5">
            {label}
            {opts.hint ? <InfoHint text={opts.hint} /> : null}
          </FormLabel>
          <FormControl>
            <Input
              type={opts.type ?? 'text'}
              placeholder={opts.placeholder}
              disabled={opts.disabled}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const select = (
    name: 'channel' | 'job_type' | 'status',
    label: string,
    options: readonly { value: string; label: string }[],
    disabled = false,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const toggle = (name: 'boosted' | 'viewed_by_client', label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex items-center gap-3 space-y-0 pt-6">
          <FormControl>
            <Switch checked={field.value} onCheckedChange={field.onChange} aria-label={label} />
          </FormControl>
          <FormLabel className="font-normal">{label}</FormLabel>
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-3xl space-y-5">
        <FormSection
          variant="card"
          title="Proposal"
          description="Which channel, which job, and who submitted it. The bidder owns the bid — that ownership flows into the lead and the deal."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {select('channel', 'Channel', BID_CHANNELS, lockKey)}
            {text('job_id', 'Job id', {
              placeholder: '~01abc… or the tender reference',
              hint: 'Channel + job id + proposal id identify the bid. Free text for non-Upwork channels. Fixed on synced rows.',
              disabled: lockKey,
            })}
            {text('proposal_id', 'Proposal id', { placeholder: 'Optional', disabled: lockKey })}
            {text('bidder_profile', 'Bidder profile', {
              placeholder: 'e.g. Flexipy agency',
              hint: 'Which channel profile submitted (founder individual, agency, …).',
            })}
          </div>
          <FormField
            control={form.control}
            name="owner"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  Owner
                  <InfoHint text="The person who submitted the bid. Commission on the resulting deal keys off this — never changed by who converts it." />
                </FormLabel>
                <FormControl>
                  <MemberSelect
                    queryKey="bid-owner-picker"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Assign the bidder"
                    roles={CRM_OWNER_ROLES}
                    clearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Job" description="What was posted.">
          {text('job_title', 'Job title')}
          {text('job_url', 'Job URL', { placeholder: 'https://…' })}
          <div className="grid gap-4 sm:grid-cols-3">
            {select('job_type', 'Job type', BID_JOB_TYPES)}
            {text('client_budget', 'Client budget', { placeholder: '$3,000 or $40–60/h' })}
            {text('experience_level', 'Experience level', { placeholder: 'Expert' })}
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Our bid"
          description="What we offered, when, and the job's bid range at that moment."
        >
          <div className="grid gap-4 sm:grid-cols-3">
            {text('submitted_at', 'Submitted', { type: 'datetime-local' })}
            {text('currency', 'Currency', { placeholder: 'USD' })}
            {jobType === 'fixed'
              ? text('bid_amount', 'Bid amount (fixed)', { placeholder: '1500.00' })
              : text('bid_rate', 'Bid rate (hourly)', { placeholder: '45.00' })}
            {text('profile_rate_at_submit', 'Profile rate at submit')}
            {text('connects_cost', 'Connects cost')}
            {text('connects_spent', 'Connects spent')}
            {text('connects_refunded', 'Connects refunded')}
            {toggle('boosted', 'Boosted')}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {text('bid_range_low', 'Job bid range · low')}
            {text('bid_range_median', 'Job bid range · median', {
              hint: 'Our bid vs this median (below / at / above) is derived and used by the roll-up.',
            })}
            {text('bid_range_high', 'Job bid range · high')}
          </div>
          <FormField
            control={form.control}
            name="bid_filter_verdict"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bid-filter verdict</FormLabel>
                <FormControl>
                  <Textarea rows={2} placeholder="Why we bid (fit-check notes)" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection variant="card" title="Client" description="What the channel shows about the client.">
          <div className="grid gap-4 sm:grid-cols-3">
            {text('client_country', 'Country', { placeholder: 'US' })}
            {text('client_hire_rate_pct', 'Hire rate %')}
            {text('client_total_spent', 'Total spent')}
          </div>
        </FormSection>

        <FormSection
          variant="card"
          title="Outcome"
          description="Synced rows update these automatically; set them here for manual bids or corrections."
        >
          <div className="grid gap-4 sm:grid-cols-3">
            {select('status', 'Status', BID_STATUSES)}
            {text('replied_at', 'Replied', { type: 'datetime-local' })}
            {toggle('viewed_by_client', 'Viewed by client')}
            {text('room_id', 'Interview room id')}
            {text('contract_id', 'Contract id', {
              hint: 'On synced rows the sync marks a bid with a contract as hired; on manual rows set the status yourself.',
            })}
          </div>
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
